import React from 'react';
import { DownloadIcon } from '@common/utils/icons/icons';
import Button from '@mui/material/Button';
import { useAppSelector } from 'store';
import { selectFleetData } from 'store/fleet-management/fleet-management-slice-selectors';
import { useTranslation } from 'react-i18next';
import { trackEvent } from 'services/analytics';
import { AnalyticsEvent } from 'services/analytics-events';

const DownloadCsvDataButton: React.FC<{ id: string; name: string }> = ({ id, name }) => {
  const { t } = useTranslation();

  const data = useAppSelector((state) => selectFleetData(state)).find((d) => d.id === id)?.data;

  if (data == null) return null;

  const downloadClickHandler = () => {
    if (data == null) return;

    const headers = Object.keys(data[0]);

    trackEvent(AnalyticsEvent.DatasetExported, {
      dataset_id: id,
      row_count: data.length,
      column_count: headers.length,
    });

    const sanitizeCsvCell = (value: unknown) => {
      const raw = value == null ? '' : String(value);
      const escaped = raw.replace(/"/g, '""');
      const guarded = /^[=+\-@]/.test(escaped) ? `'${escaped}` : escaped;
      return `"${guarded}"`;
    };

    const csvRows = [
      headers.join(','), // header row
      ...data.map((item: Record<string, unknown>) =>
        headers.map((header) => sanitizeCsvCell(item[header])).join(','),
      ),
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const stringifiedDashboardName = name.split(' ').join('-');

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', stringifiedDashboardName + '-data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <React.Fragment>
      <Button onClick={downloadClickHandler} disabled={data == null} startIcon={<DownloadIcon />}>
        {t('button-download-csv')}
      </Button>
    </React.Fragment>
  );
};

export default DownloadCsvDataButton;
